import styled from "styled-components";

export const Page = styled.div`
  min-height: 100vh;
  background-color: #f7f4f0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 20px 80px;

  @media (max-width: 600px) {
    padding: 32px 16px 64px;
  }
`;

export const LogoImg = styled.img`
  height: 40px;
  margin-bottom: 40px;
  object-fit: contain;
`;

export const Card = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 40px 44px;
  width: 100%;
  max-width: 620px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.07);

  @media (max-width: 600px) {
    padding: 28px 20px;
  }
`;

export const StepLabel = styled.p`
  font-family: "Aileron", sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #888888;
  margin: 0 0 6px 0;
`;

export const StepTitle = styled.h2`
  font-family: "Poppins", sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #000000;
  margin: 0 0 6px 0;
  line-height: 1.2;

  @media (max-width: 600px) {
    font-size: 20px;
  }
`;

export const StepSubtitle = styled.p`
  font-family: "Aileron", sans-serif;
  font-size: 13px;
  color: #666666;
  margin: 0 0 28px 0;
  line-height: 1.5;
`;

export const ProgressBar = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 32px;
`;

export const ProgressDot = styled.div<{ active: boolean; done: boolean }>`
  height: 4px;
  flex: 1;
  border-radius: 4px;
  background-color: ${({ active, done }) =>
    done ? "#000000" : active ? "#3487d3" : "#e0e0e0"};
  transition: background-color 0.2s ease;
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 8px;
`;

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const Label = styled.label`
  font-family: "Aileron", sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #444444;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #dddddd;
  border-radius: 8px;
  font-family: "Aileron", sans-serif;
  font-size: 13px;
  color: #222222;
  background: #ffffff;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: #aaaaaa;
  }

  &:focus {
    border-color: #000000;
  }

  &:disabled {
    background: #f5f5f5;
    color: #888888;
    cursor: not-allowed;
  }
`;

export const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #dddddd;
  border-radius: 8px;
  font-family: "Aileron", sans-serif;
  font-size: 13px;
  color: #222222;
  background: #ffffff;
  outline: none;
  box-sizing: border-box;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: #aaaaaa;
  }

  &:focus {
    border-color: #000000;
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #dddddd;
  border-radius: 8px;
  font-family: "Aileron", sans-serif;
  font-size: 13px;
  color: #222222;
  background: #ffffff;
  outline: none;
  box-sizing: border-box;
  appearance: none;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: #000000;
  }
`;

export const HelperText = styled.p`
  font-family: "Aileron", sans-serif;
  font-size: 11px;
  color: #888888;
  margin: 0;
  line-height: 1.4;
`;

export const ErrorText = styled.p`
  font-family: "Aileron", sans-serif;
  font-size: 12px;
  color: #dc2626;
  margin: 6px 0 0 0;
`;

export const AmenitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;

  @media (max-width: 500px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const AmenityChip = styled.button<{ selected: boolean }>`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ selected }) => (selected ? "#000000" : "#dddddd")};
  background: ${({ selected }) => (selected ? "#000000" : "#ffffff")};
  color: ${({ selected }) => (selected ? "#ffffff" : "#333333")};
  font-family: "Aileron", sans-serif;
  font-size: 11px;
  font-weight: ${({ selected }) => (selected ? "600" : "400")};
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: center;

  &:hover {
    border-color: #000000;
  }
`;

export const ImageUploadGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
  margin-bottom: 8px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const ImageSlot = styled.div<{ hasImage: boolean }>`
  aspect-ratio: 4/3;
  border-radius: 10px;
  border: 2px dashed ${({ hasImage }) => (hasImage ? "transparent" : "#cccccc")};
  background: ${({ hasImage }) => (hasImage ? "transparent" : "#fafafa")};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${({ hasImage }) => (hasImage ? "transparent" : "#999999")};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ImageSlotPlus = styled.span`
  font-size: 22px;
  color: #cccccc;
`;

export const ImageRemoveBtn = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #ffffff;
  border: none;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

export const LogoUploadArea = styled.div<{ hasImage: boolean }>`
  width: 120px;
  height: 120px;
  border-radius: 12px;
  border: 2px dashed ${({ hasImage }) => (hasImage ? "transparent" : "#cccccc")};
  background: ${({ hasImage }) => (hasImage ? "transparent" : "#fafafa")};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  margin-bottom: 8px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 8px;
    box-sizing: border-box;
  }
`;

export const NavRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
  gap: 12px;
`;

export const PrimaryButton = styled.button`
  background: #000000;
  color: #ffffff;
  border: none;
  border-radius: 24px;
  padding: 12px 28px;
  font-family: "Aileron", sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  min-width: 120px;

  &:hover:not(:disabled) {
    background: #222222;
  }

  &:disabled {
    background: #aaaaaa;
    cursor: not-allowed;
  }
`;

export const SecondaryButton = styled.button`
  background: transparent;
  color: #333333;
  border: 1px solid #cccccc;
  border-radius: 24px;
  padding: 12px 28px;
  font-family: "Aileron", sans-serif;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #000000;
    color: #000000;
  }
`;

export const ReviewSection = styled.div`
  margin-bottom: 20px;
`;

export const ReviewTitle = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #000000;
  margin: 0 0 6px 0;
`;

export const ReviewRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
  font-family: "Aileron", sans-serif;
  font-size: 12px;
  color: #444444;
  gap: 12px;

  span:last-child {
    text-align: right;
    color: #000000;
    font-weight: 600;
  }
`;

export const SuccessCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 56px 44px;
  width: 100%;
  max-width: 540px;
  text-align: center;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.07);
`;

export const SuccessIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

export const SuccessTitle = styled.h2`
  font-family: "Poppins", sans-serif;
  font-size: 26px;
  font-weight: 700;
  color: #000000;
  margin: 0 0 12px 0;
`;

export const SuccessText = styled.p`
  font-family: "Aileron", sans-serif;
  font-size: 14px;
  color: #555555;
  margin: 0;
  line-height: 1.6;
`;

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid #f0f0f0;
  margin: 20px 0;
`;

export const PasswordToggleWrapper = styled.div`
  position: relative;
`;

export const PasswordToggleBtn = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #888888;
  font-size: 12px;
  padding: 0;
  font-family: "Aileron", sans-serif;
`;

export const InfoCallout = styled.div`
  background-color: #fef9ec;
  border: 1px solid #f5d97a;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 24px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

export const InfoCalloutIcon = styled.span`
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 1px;
`;

export const InfoCalloutText = styled.p`
  font-family: "Aileron", sans-serif;
  font-size: 12px;
  color: #7a5c00;
  margin: 0;
  line-height: 1.55;

  strong {
    font-weight: 700;
    color: #5c4400;
  }
`;

export const CredentialBox = styled.div`
  background: #f7f4f0;
  border: 1px solid #e0dbd4;
  border-radius: 10px;
  padding: 14px 16px;
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const AutocompleteWrapper = styled.div`
  position: relative;
`;

export const SuggestionList = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #dddddd;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  margin: 0;
  padding: 4px 0;
  list-style: none;
  z-index: 100;
  max-height: 220px;
  overflow-y: auto;
`;

export const SuggestionItem = styled.li`
  padding: 10px 12px;
  font-family: "Aileron", sans-serif;
  font-size: 13px;
  color: #222222;
  cursor: pointer;
  line-height: 1.3;

  &:hover {
    background: #f5f5f5;
  }
`;

export const CredentialRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: "Aileron", sans-serif;
  font-size: 13px;
  gap: 12px;

  span:first-child {
    color: #888888;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    flex-shrink: 0;
  }

  span:last-child {
    color: #111111;
    font-weight: 600;
    word-break: break-all;
    text-align: right;
  }
`;
